import {
  caseEntityTypesMap,
  replacements as defaultReplacements,
} from "./constants"
import i18n from "@shared/plugins/i18n";

const propertiesToSkip = []
const propertyEndingsToSkip = [
  "id",
]
const valuesToSkipChecks = [
  (prop) => typeof prop !== "string",
  (prop) => !prop || !prop.length,
]
const propertiesToSkipChecks = [
  (prop) => propertyEndingsToSkip.some((part) => prop.endsWith(part)),
  (prop) => propertiesToSkip.includes(prop),
]

const formEntityKeyGenerator = (propValue, entityType) => `${entityType}.${replaceWithMap(propValue)}`

/**
 * Generates translation keys for a given entity type and its fields.
 * @param {object} entity - The entity object containing the fields to be translated.
 * @param {string} entityType - The type of the entity (e.g., 'section', 'block', etc.).
 * @param {Array} exceptions - An array of callback functions to skip certain properties or values.
 * @returns {object} An object mapping field names to their respective translation keys.
 */
export const getEntityPropsTranslations = (entity, entityType = caseEntityTypesMap.FIELD, exceptions = []) => {
  if (!entity) return {};

  const propsToTranslate = Object.entries(entity)
    .filter(([key, value]) => !propertiesToSkipChecks.some((check) => check(key)) && !valuesToSkipChecks.some((check) => check(value)))
    .filter(([, value]) => !exceptions.some((check) => check(value)))

  return propsToTranslate.reduce((acc, [prop, propValue]) => {
    const key = formEntityKeyGenerator(propValue, entityType);
    acc[prop] = i18n.t(key) || entity[prop];
    return acc;
  }, {});
}

export const translateEntity = (entity, entityType = caseEntityTypesMap.FIELD) => {
  if (!entity) return {};

  const translatedEntity = getEntityPropsTranslations(entity, entityType);
  return { ...entity, ...translatedEntity };
}

/**
 * Replaces characters in a string based on a provided mapping.
 * @param {string} input - The input string to be modified.
 * @param {Array} replacements - An array of character pairs to be replaced.
 * @param {boolean} reverse - If true, reverses the mapping (i.e., replaces 'to' with 'from').
 * @returns {string} The modified string with characters replaced.
 */
export function replaceWithMap(input, replacements = defaultReplacements, reverse = false) {
  if (!input) return '';
  const actualReplacements = reverse
    ? replacements.map(([from, to]) => [to, from])
    : replacements;

  const pattern = new RegExp(
    `[${actualReplacements.map(([from]) => from.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('')}]`,
    'g'
  );

  const replaceMap = Object.fromEntries(actualReplacements);

  return input.replace(pattern, match => replaceMap[match] ?? match);
}